import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { cx, ui, focus, interaction } from '../../lib/ui-primitives'

// ============================================
// BUTTON VARIANTS
// Centralized button styles extracted from 6+ screens
// ============================================

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'quiet' | 'danger'
export type ButtonSize = 'sm' | 'md' | 'lg'

const variantStyles: Record<ButtonVariant, string> = {
  // Primary: Solid background with crypto color (set via style prop)
  primary: cx(
    'text-white font-semibold',
    'hover:brightness-110',
    interaction.hoverScale,
  ),
  // Secondary: Outlined/ghost with indigo accent
  secondary: cx(
    'bg-indigo-600 hover:bg-indigo-500 text-white',
    interaction.hoverScale,
  ),
  // Ghost: Subtle background for secondary actions
  ghost: cx(
    ui.btnGhost,
  ),
  // Quiet: Minimal styling for inline actions
  quiet: cx(
    ui.btnQuiet,
  ),
  // Danger: Destructive actions
  danger: cx(
    ui.btnDanger,
  ),
}

const sizeStyles: Record<ButtonSize, string> = {
  sm: 'px-3 py-2 text-sm min-h-[40px]',
  md: 'px-4 py-3 text-base min-h-[48px]',
  lg: 'px-6 py-4 text-lg min-h-[56px] font-bold',
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Visual variant */
  variant?: ButtonVariant
  /** Size preset */
  size?: ButtonSize
  /** Full width button */
  fullWidth?: boolean
  /** Loading state */
  loading?: boolean
  /** Icon before text */
  leftIcon?: ReactNode
  /** Icon after text */
  rightIcon?: ReactNode
  /** Custom background color (for primary variant with crypto colors) */
  bgColor?: string
}

/**
 * Button - Unified button component with physics-based interactions
 *
 * Extracted from: WelcomeScreen, DetailsScreen, AmountScreen, PaymentScreen,
 * ConfirmScreen, ProcessingScreen, LookupScreen
 *
 * Features:
 * - 5 variants covering all use cases
 * - 3 size presets (all meet 44px+ touch target)
 * - Optional icon slots
 * - Loading state with spinner
 * - Custom background color for crypto-branded buttons
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'ghost',
      size = 'md',
      fullWidth = false,
      loading = false,
      leftIcon,
      rightIcon,
      bgColor,
      className,
      disabled,
      children,
      style,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading

    return (
      <button
        ref={ref}
        disabled={isDisabled}
        className={cx(
          // Base styles
          'inline-flex items-center justify-center gap-2 rounded-xl font-medium',
          'transition-all',
          focus.ring,
          interaction.disabled,

          // Variant
          variantStyles[variant],

          // Size
          sizeStyles[size],

          // Full width
          fullWidth && 'w-full',

          // Custom overrides
          className
        )}
        style={{
          // Allow custom background color for primary variant
          ...(bgColor && variant === 'primary' ? { backgroundColor: bgColor } : {}),
          ...style,
        }}
        {...props}
      >
        {/* Loading spinner replaces left icon */}
        {loading ? (
          <span
            className="size-4 border-2 border-current/30 border-t-current rounded-full animate-spin"
            aria-hidden="true"
          />
        ) : leftIcon ? (
          <span className="shrink-0" aria-hidden="true">
            {leftIcon}
          </span>
        ) : null}

        {children}

        {rightIcon && !loading && (
          <span className="shrink-0" aria-hidden="true">
            {rightIcon}
          </span>
        )}
      </button>
    )
  }
)

Button.displayName = 'Button'
